'use strict'
import { type LoadRequest, type ContentType, type ManifestConfiguration } from '@foundation-player/api'
import { type VideoModeValues } from '@/types/cms'
import { type VideoPlayerProps } from '@/types/video'
import { getBreakpoint } from '@/utils/app/detect'

type VideoModeAttr = {
  readonly mediaId?: string
  readonly mediaType: ContentType
  readonly videoService: string
  readonly contentType: 'video' | 'videolivestream' | 'videoevent'
}

type VideoModeMap = Record<VideoModeValues, VideoModeAttr>

const videoModeMap: VideoModeMap = {
  vod: {
    mediaType: 'vod',
    videoService: 'ntv',
    contentType: 'video',
  },
  livestream: {
    mediaType: 'live',
    videoService: 'lvstr_ntv',
    contentType: 'videolivestream',
  },
  eventstream1world: {
    mediaId: 'ntvevent01',
    mediaType: 'event',
    videoService: 'lvstr_ntve',
    contentType: 'videoevent',
  },
  eventstream1de: {
    mediaId: 'ntvevent01de',
    mediaType: 'event',
    videoService: 'lvstr_ntve',
    contentType: 'videoevent',
  },
  eventstream2world: {
    mediaId: 'ntvevent02',
    mediaType: 'event',
    videoService: 'lvstr_ntve',
    contentType: 'videoevent',
  },
  eventstream2de: {
    mediaId: 'ntvevent02de',
    mediaType: 'event',
    videoService: 'lvstr_ntve',
    contentType: 'videoevent',
  },
}

export const createAdCall = (category: string, id: string, conlen?: number) => {
  const baseUrl = 'https://ad-ipd.sxp.smartclip.net/select'

  const params = {
    type: 'vast4', // Definition der AdserverResponse
    fwd_adb: '[BLOCKED]', // placeholder
    s: category, //???
    sz: 'pre', // für Preroll. anpassen, wenn es weitere Slots gibt
    tagver: '{TAGVER}', // placeholder, Wert kommt aus dem Player
    fwd_comp: '0', // sind Companion Ads erlaubt? (1 ja, 0 nein?)
    ang_cntp: 'none', // Name des contentpartner
    ang_streamid: window.crypto && crypto.randomUUID ? crypto.randomUUID() : '', // random UUID
    ...(conlen ? { ang_conlen: conlen.toString() } : {}), // Content length, Länge des Content Videos in Sekunden
    fwd_conid: id, // Video ID
    rnd: (Math.floor(Math.random() * (9999999999999998 - 10000000)) + 10000000).toString(), // positive Zufallszahl, mindestens 8 Zeichen(i64)
    ang_plrw: '{PLAYER_WIDTH}', // Player Breite placeholder
    ang_plrh: '{PLAYER_HEIGHT}', // Player Hoehe placeholder
    consent: '{CONSENT}', // placeholder
  }

  let adCall = baseUrl + '?'
  for (const key in params) {
    adCall += key + '=' + params[key as keyof typeof params] + '&'
  }

  if (window.$AC && typeof window.$AC.setTargeting == 'function' && typeof window.$AC.getSXPKeys === 'function') {
    if (conlen) window.$AC.setTargeting('vcd', conlen)
    adCall += window.$AC.getSXPKeys()
  }

  return adCall
}

export const createLoadRequest = ({
  videoProps,
  isPurMode,
  isPreview,
  consentString,
}: {
  videoProps: VideoPlayerProps
  isPurMode: boolean
  isPreview: boolean
  consentString?: string
}): LoadRequest => {
  const videoMode = videoProps.mode as keyof typeof videoModeMap
  const commentPath = (getBreakpoint() === 'xs' ? 'ntv-mew' : 'ntv') + '/' + videoProps.trackingPath
  return {
    contentMetadata: {
      posterUrl: videoProps.poster,
      headline: videoProps.title,
      title: videoProps.title,
      channelName: 'ntv',
      firstPublicationDate: videoProps.publishedAt,
    },
    contentType: videoModeMap[videoMode].mediaType,
    contentId: videoModeMap[videoMode].mediaId ?? videoProps.id,
    autoplay: videoProps.autoplay ?? false,
    ...(consentString ? { user: { consentString: consentString } } : {}),
    remoteConfig: {
      clientIdentifier: 'html5/rtldigitalnews/ntv',
      environment: location.host.includes('staging-www.') ? 'preprod' : 'prod',
    },
    plugins: {
      'foundation.plugin.heartbeat': {
        enabled: !isPurMode,
        kidsProfile: false,
        offer: getBreakpoint() === 'xs' ? 'mobntv' : 'ntv',
        payStatus: 0,
        userStatus: 0,
        ivw: '/' + commentPath,
        startType: videoProps.heartbeatStarttype ?? 0, // 1 für autom. videostart (playlist)
        videoService: videoModeMap[videoMode].videoService,
      },
      'foundation.plugin.googleAnalytics4': {
        enabled: !isPurMode,
        progressTrackingIntervalInSeconds: 15,
        contentType: videoModeMap[videoMode].contentType,
        keywords: videoProps.keywords,
      },
      'foundation.plugin.nielsen': {
        enabled: !isPurMode,
        commentPath: commentPath,
      },
      'foundation.plugin.npaw': {
        enabled: !isPurMode,
      },
      'foundation.plugin.smartclip': {
        enabled: !(isPurMode || isPreview || videoProps.disableAds),
        smartclipConfig: {
          adReinsertion: {
            homad: {
              enabled: true,
              setup: {
                clientConfig: '/sport/configs/videoPlayer/homadClientConfig.json',
              },
            },
          },
        },
        adSlots: [
          {
            offsetInSeconds: 0,
            type: 'linear',
            adCall: createAdCall(videoProps.category, videoProps.id, videoProps.lengthSeconds || undefined),
          },
        ],
      },
    },
    manifests: [
      ...(videoProps.source.progressive
        ? [
            {
              type: 'progressive',
              sources: [
                {
                  url: videoProps.source.progressive,
                },
              ],
            } as ManifestConfiguration,
          ]
        : []),
      ...(videoProps.source.dash
        ? [
            {
              type: 'dash',
              sources: [
                {
                  url: videoProps.source.dash,
                },
              ],
              licenseServers: [
                {
                  url: 'https://widevine.n-tv.de/index/proxy',
                  type: 'widevine',
                },
                {
                  url: 'https://playready-core.n-tv.de/playready/api/RightsManager.asmx',
                  type: 'playready',
                },
              ],
            } as ManifestConfiguration,
          ]
        : []),
      ...(videoProps.source.hls
        ? [
            {
              type: 'hls',
              sources: [
                {
                  url: videoProps.source.hls,
                },
              ],
              licenseServers: [
                {
                  url: 'https://fairplay.n-tv.de/Fairplay/GetLicense',
                  certificate: 'https://fairplay.n-tv.de/Certificate/RTLiDe_cert.cer',
                  type: 'fairplay',
                },
              ],
            } as ManifestConfiguration,
          ]
        : []),
    ],
  }
}
